import { type TrackerMessage, TrackerMessagesSchema } from '@racemap/sdk/schema/trackers';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import type { ObjectId } from '@racemap/utilities/types/utils';
import { useCallback, useMemo } from 'react';
import { mutate } from 'swr';
import useSWRInfinite, { unstable_serialize } from 'swr/infinite';

const apiClient = RacemapAPIClient.fromWindowLocation();

const PAGE_SIZE = 25;
const KEY_PREFIX = 'TRACKER_MESSAGES';

type Key = [typeof KEY_PREFIX, string, string | undefined];

interface Page {
  messages: Array<TrackerMessage>;
  nextCursor: string | undefined;
  hasMore: boolean;
  totalCount: number | undefined;
}

const trackerMessagesKey =
  (id: string) =>
  (pageIndex: number, previousPageData: Page | null): Key | null => {
    if (pageIndex === 0) return [KEY_PREFIX, id, undefined];
    if (!previousPageData?.hasMore) return null;
    return [KEY_PREFIX, id, previousPageData.nextCursor];
  };

interface PagedMessages {
  messages: Array<TrackerMessage>;
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  totalCount: number | undefined;
  error: Error | undefined;
  loadMore: () => void;
}

const fetcher = async ([, trackerId, cursor]: Key): Promise<Page> => {
  const result = await apiClient.listTrackerMessages({
    trackerId,
    limit: String(PAGE_SIZE),
    cursor,
    includeTotal: cursor == null,
  });
  return {
    messages: TrackerMessagesSchema.parse(result.data),
    nextCursor: result.nextCursor,
    hasMore: result.hasMore,
    totalCount: result.totalCount,
  };
};

export function useTrackerMessagesPaged(
  trackerId: ObjectId | string | null | undefined,
): PagedMessages {
  const id = trackerId?.toString();

  const getKey = useCallback(
    (pageIndex: number, previousPageData: Page | null): Key | null =>
      id == null ? null : trackerMessagesKey(id)(pageIndex, previousPageData),
    [id],
  );

  const { data, error, isLoading, isValidating, setSize } = useSWRInfinite<Page, Error>(
    getKey,
    fetcher,
    { revalidateAll: false, keepPreviousData: true },
  );

  const loadMore = useCallback(() => {
    void setSize((s) => s + 1);
  }, [setSize]);

  const messages = useMemo(() => data?.flatMap((p) => p.messages) ?? [], [data]);
  const isFetchingMore = isValidating && !isLoading;
  const hasMore = data?.at(-1)?.hasMore ?? false;
  const totalCount = data?.[0]?.totalCount;

  return {
    messages,
    isLoading,
    isFetchingMore,
    hasMore,
    totalCount,
    error,
    loadMore,
  };
}

export function revalidateTrackerMessages(trackerIds: Array<string | ObjectId>): Promise<unknown> {
  const ids = [...new Set(trackerIds.map((id) => id.toString()))];
  return Promise.all(ids.map((id) => mutate(unstable_serialize(trackerMessagesKey(id)))));
}
